import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { BaseRequestModel } from '../models/base-request-result.model';
import { ContractByUser, ContractDetail } from '../models/contract.model';

@Injectable({
	providedIn: 'root',
})
export class ContractsService {
	constructor(private readonly http: HttpClient) {}

	public createCommercialPreContract(basketId: string, payload: any): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}contracts/pre-contract/baskets/${basketId}`, payload);
	}

	public editSign(basketId: string, payload: any): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}update-contract/baskets/${basketId}`, payload);
	}

	public confirmSign(basketId: string, payload: any): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}contracts/baskets/${basketId}`, payload);
	}

	public reconsiderProposal(payload: any): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}contracts/reconcide`, payload);
	}

	public getOrders(userId: string): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}contracts/users/${userId}/requests/headers`);
	}

	public completeDocuSign(contractId: string): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}contracts/${contractId}/signatures/complete`, {});
	}

	public getContracts(userId: string) {
		return this.http.get<BaseRequestModel<Array<ContractByUser>>>(`${environment.apiUrl}contracts/users/${userId}/headers`);
	}

	public getContractsWithPayments(userId: string): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}contracts/users/${userId}/headers-with-payments`);
	}

	public getOrdersHeaders(userId: string): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}contracts/users/${userId}/headers`);
	}

	public getOrderById(id: string) {
		return this.http.get<BaseRequestModel<ContractDetail>>(`${environment.apiUrl}contracts/${id}`);
	}

	public getPaymentInfos(id: string): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}contracts/${id}/payments`);
	}

	public getDevicesContractById(id: string): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}contracts/${id}/devices`);
	}

	public getDevicesByPlate(plate: string, rentId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}contracts/${plate}/devices-by-plate`, {
        params: { rentId }
    });
}

	public getPendingDocuments(id: string): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}contracts/${id}/pending-documents`);
	}

	public savePendingDocuments(id: string): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}contracts/${id}/pending-documents`, {});
	}

	public goToSignature(id: string, payload: any): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}contracts/${id}/signatures/links`, payload);
	}

	public saveBeneficiaries(id: string, payload: any): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}contracts/${id}/insurances/beneficiaries`, payload);
	}

	public contractPayment(payload: any): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}payments/contract-payments`, payload);
	}

	public contractPrePayment(payload: any): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}payments/contract-prepayment`, payload);
	}

	public contractMonthly(payload: any): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}payments/contract-monthly`, payload);
	}

	public updateInvoice(payload: any): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}payments/invoice`, payload);
	}

	public getDealerProposals(dealershipId: string, page: number, itemsPerPage: number, filters: any): Observable<any> {
		let params = new HttpParams();
		Object.keys(filters).forEach(key => {
			const value = filters[key];
			if (value !== null && value !== undefined && value !== '') {
				params = params.append(key, String(value));
			} else {
				params = params.delete(key);
			}
		});
		return this.http.get<any>(`${environment.apiUrl}contracts/dealerships/${dealershipId}/proposals?itemsPerPage=${itemsPerPage}&page=${page}`, { params });
	}

	public getSellerProposals(origem: string, page: number, itemsPerPage: number, filters: any, isVE?: boolean): Observable<any> {
		let params = new HttpParams();
		Object.keys(filters).forEach(key => {
			const value = filters[key];
			if (value !== null && value !== undefined && value !== '') {
				params = params.append(key, String(value));
			} else {
				params = params.delete(key);
			}
		});
		return this.http.get<any>(`${environment.apiUrl}contracts/${isVE ? 've/sellers' : 'sellers'}/${origem.toUpperCase()}/proposals?itemsPerPage=${itemsPerPage}&page=${page}`, { params });
	}

	public getVeCombinedProposalsCount(
		origem: string,
		filters: any
	): Observable<any> {
		let params = new HttpParams();
		Object.keys(filters).forEach(key => {
			const value = filters[key];
			if (value !== null && value !== undefined && value !== '') {
				params = params.append(key, String(value));
			} else {
				params = params.delete(key);
			}
		});
		return this.http.get<any>(
			`${environment.apiUrl}contracts/ve/combined/${origem.toUpperCase()}/proposals/count`,
			{ params }
		);
	}

	public getVeCombinedProposals(
		origem: string,
		page: number,
		itemsPerPage: number,
		filters: any
	): Observable<any> {
		let params = new HttpParams();
		Object.keys(filters).forEach(key => {
			const value = filters[key];
			if (value !== null && value !== undefined && value !== '') {
				params = params.append(key, String(value));
			} else {
				params = params.delete(key);
			}
		});
		return this.http.get<any>(
			`${environment.apiUrl}contracts/ve/combined/${origem.toUpperCase()}/proposals?itemsPerPage=${itemsPerPage}&page=${page}`,
			{ params }
		);
	}

	public getProposalDetail(id: string, isVE: boolean, isSimulation = false): Observable<any> {
		if (isVE && isSimulation) {
			return this.http.get<any>(`${environment.apiUrl}contracts/${id}/proposals`);
		}

		const base = isVE ? 'contracts/ka' : 'contracts';
		const tail = isVE ? 'proposals-ka' : 'proposals';

		return this.http.get<any>(`${environment.apiUrl}${base}/${id}/${tail}`);
	}

	public cancelInsurance(id: string, insuranceId: string): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}contracts/${id}/cancel-insurance/${insuranceId}`, {});
	}

	public cancelProposal(rentId: string, reasonCode: string): Observable<any> {
  const payload = {
    rentId: rentId,
    reasonCode: reasonCode
  };
  return this.http.post<any>(`${environment.apiUrl}contracts/cancel`, payload);
}

	public downloadFileDevice(contractId: string, deviceId: string, fileType: number): Observable<HttpResponse<any>> {
		return this.http.get<any>(`${environment.apiUrl}files/contracts/${contractId}/devices/${deviceId}/documents/${fileType}`, {
			observe: 'response',
			responseType: 'blob' as 'json',
		});
	}

	public downloadFileContract(contractId: string, fileType: number): Observable<HttpResponse<any>> {
		return this.http.get<any>(`${environment.apiUrl}files/contracts/${contractId}/documents/${fileType}`, {
			observe: 'response',
			responseType: 'blob' as 'json',
		});
	}

	public getProposalStatusOptions(): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}contracts/proposal-status-options`);
	}

	public updateContractOrigin(contractId: string, origin: string): Observable<any> {
		return this.http.put<any>(`${environment.apiUrl}contracts/${contractId}/origin`, { origin });
	}
	public getDeliveryConditions(contractId: string): Observable<any> {
  return this.http.get<any>(`${environment.apiUrl}contracts/${contractId}/delivery-conditions`);
}

public saveDeliveryCondition(contractId: string, payload: any): Observable<any> {
  return this.http.post<any>(`${environment.apiUrl}contracts/${contractId}/delivery-conditions/save`, payload);
}

public sendDeliveryCondition(contractId: string, payload: any): Observable<any> {
  return this.http.post<any>(`${environment.apiUrl}contracts/${contractId}/delivery-conditions/send`, payload);
}
}
